import { API_ROUTES } from '../shared/apiRoutes/apiRoutes';
import {
  fetchUsersFromAuth,
  fetchWithAuth,
  getEventFlows,
  getSites,
  getTenantPagingZonesAdmin,
  getTenantAgentsHealth,
  getTenantDevices,
} from '../api/backendApi';
import { fetchAllTenantAgents } from '../api/agentApi';

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  return [];
};

const normalizeApiArray = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response)) return response;
  return [];
};

const tryOrFallback = async (fetcher, fallbackValue) => {
  try {
    return await fetcher();
  } catch (error) {
    return fallbackValue;
  }
};

const fetchActions = async (tenantId) => {
  const endpoint = `${API_ROUTES.ACTIONS}?tenantId=${encodeURIComponent(tenantId)}`;
  const response = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  return normalizeApiArray(response);
};

const fetchSipConfig = async (tenantId) => {
  const endpoint = `/api/v2/tenants/${encodeURIComponent(tenantId)}/sip-config`;
  const response = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  if (response?.success && response?.data) return response.data;
  return response?.data || response || null;
};

const fetchIncomingEvents = async (tenantId) => {
  const params = new URLSearchParams({ tenantId, limit: '50' });
  const endpoint = `/api/incoming-events/list?${params.toString()}`;
  const response = await fetchWithAuth(endpoint, 'GET', null, { silent: true });
  return normalizeApiArray(response);
};

const normalizeFlows = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.flows)) return response.data.flows;
  if (Array.isArray(response?.flows)) return response.flows;
  return [];
};

const normalizeSites = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.sites)) return response.sites;
  return [];
};

export function dashboardAgentMetric(agentsError, activeAgents, agentCount) {
  if (agentsError) {
    return { primary: 'Unavailable', secondary: 'Agent list failed', failed: true };
  }
  const inactive = Math.max(0, Number(agentCount || 0) - Number(activeAgents || 0));
  return {
    primary: `${activeAgents} online`,
    secondary: `${inactive} offline`,
    failed: false,
  };
}

const normalizeAgentHealthStatus = (rawStatus) => {
  const status = String(rawStatus || '').trim().toUpperCase();
  if (!status) return 'OFFLINE';

  if (status === 'HEALTHY') return 'ONLINE';
  if (status === 'STALE') return 'DEGRADED';
  return status;
};

export const fetchDashboardData = async (tenantId) => {
  if (!tenantId) {
    return {
      tenantId: null,
      counts: { sites: 0, users: 0, agents: 0, devices: 0, flows: 0, actions: 0 },
      sites: [],
      users: [],
      agents: [],
      devices: [],
      flows: [],
      actions: [],
      agentHealth: { active: 0, inactive: 0, byAgentId: {} },
      sip: { configured: false, config: null },
      incomingEvents: [],
      fetchedAt: new Date().toISOString(),
    };
  }

  const [
    sitesResponse,
    usersResponse,
    agentsResponse,
    devicesResponse,
    flowsResponse,
    actionsResponse,
    agentHealthResponse,
    sipConfig,
    pagingZones,
    incomingEvents,
  ] = await Promise.all([
    tryOrFallback(() => getSites(tenantId), { data: [] }),
    tryOrFallback(() => fetchUsersFromAuth({ tenantId }), { data: [] }),
    fetchAllTenantAgents(tenantId).then(
      (result) => ({ agents: result.agents, agentsError: null }),
      (error) => ({ agents: [], agentsError: error?.message || 'Failed to load agents' }),
    ),
    tryOrFallback(() => getTenantDevices(tenantId), []),
    tryOrFallback(() => getEventFlows(tenantId), { data: [] }),
    tryOrFallback(() => fetchActions(tenantId), []),
    tryOrFallback(() => getTenantAgentsHealth(tenantId, { maxAgeMs: 0 }), { data: { agents: [] } }),
    tryOrFallback(() => fetchSipConfig(tenantId), null),
    tryOrFallback(() => getTenantPagingZonesAdmin(tenantId), []),
    tryOrFallback(() => fetchIncomingEvents(tenantId), []),
  ]);

  const sites = normalizeSites(sitesResponse);
  const users = normalizeApiArray(usersResponse);
  const agents = toArray(agentsResponse?.agents);
  const agentsError = agentsResponse?.agentsError || null;
  const devices = toArray(devicesResponse);
  const flows = normalizeFlows(flowsResponse);
  const actions = toArray(actionsResponse);
  const healthAgents = toArray(agentHealthResponse?.data?.agents);

  const healthByAgentId = healthAgents.reduce((acc, agent) => {
    if (agent?.agentId) {
      const normalizedHealth = normalizeAgentHealthStatus(
        agent.healthStatus || agent.health_status || agent.health || agent.status
      );

      acc[agent.agentId] = {
        healthStatus: normalizedHealth,
        lastHeartbeatAt: agent.lastHeartbeatAt || null,
        mqttConnected: agent.mqttConnected === true,
      };
    }
    return acc;
  }, {});

  const activeAgents = Object.values(healthByAgentId).filter(({ healthStatus }) =>
    ['ONLINE', 'DEGRADED'].includes(String(healthStatus || '').toUpperCase())
  ).length;

  const counts = {
    sites: sites.length,
    users: users.length,
    agents: agents.length,
    devices: devices.length,
    flows: flows.length,
    actions: actions.length,
  };

  return {
    tenantId,
    counts,
    sites,
    users,
    agents,
    agentsError,
    devices,
    flows,
    actions,
    agentHealth: {
      active: activeAgents,
      inactive: Math.max(0, agents.length - activeAgents),
      byAgentId: healthByAgentId,
    },
    sip: {
      configured: Boolean(sipConfig),
      config: sipConfig,
    },
    voiceSettings: null,
    pagingZones: toArray(pagingZones),
    incomingEvents: toArray(incomingEvents),
    fetchedAt: new Date().toISOString(),
  };
};
